import { useEffect, useState } from "react";

import { PODCAST_CONFIG } from "./podcast-config";
import {
  parsePodcastEpisodes,
  PODCAST_RSS_REFRESH_INTERVAL_MS,
  type PodcastFeedState,
} from "./podcast-rss";

export function usePodcastFeed() {
  const [feed, setFeed] = useState<PodcastFeedState>({
    status: "loading",
    episodes: [],
  });

  useEffect(() => {
    if (!PODCAST_CONFIG.rssUrl) {
      setFeed({ status: "error", episodes: [] });
      return;
    }

    let disposed = false;
    const controllers = new Set<AbortController>();

    async function loadEpisodes() {
      const controller = new AbortController();
      controllers.add(controller);

      try {
        const response = await fetch(PODCAST_CONFIG.rssUrl!, {
          signal: controller.signal,
          cache: "no-store",
          headers: { Accept: "application/rss+xml, application/xml, text/xml" },
        });
        if (!response.ok) throw new Error(`RSS ${response.status}`);

        const rssEpisodes = parsePodcastEpisodes(await response.text());
        const episodes = rssEpisodes.map((episode) => {
          const editorial = PODCAST_CONFIG.episodes.find(
            (configured) => configured.id === episode.id,
          );
          return editorial
            ? {
                ...episode,
                ...editorial,
                description: episode.description || editorial.description,
              }
            : episode;
        });
        for (const editorial of PODCAST_CONFIG.episodes) {
          if (!episodes.some((episode) => episode.id === editorial.id)) {
            episodes.push(editorial);
          }
        }
        episodes.sort(
          (first, second) =>
            new Date(second.publishedAt).getTime() - new Date(first.publishedAt).getTime(),
        );
        if (!disposed) setFeed({ status: "ready", episodes });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (!disposed) {
          setFeed((current) =>
            current.episodes.length > 0 ? current : { status: "error", episodes: [] },
          );
        }
      } finally {
        controllers.delete(controller);
      }
    }

    void loadEpisodes();
    const refreshInterval = window.setInterval(loadEpisodes, PODCAST_RSS_REFRESH_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(refreshInterval);
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  return feed;
}
